import { Button, Grid } from "@material-ui/core";
import {
    Championship,
    Collections,
    Incident,
    Infraction,
    StatusIncident,
    useCollection,
} from "api";
import { Form, Formik, FormikHelpers } from "formik";
import { useSnackbar } from "notistack";
import React, { useMemo } from "react";
import { ExtendedFirestoreInstance, useFirestore } from "react-redux-firebase";
import {
    BoxCard,
    InputFieldFormik,
    SelectFieldFormik,
    SelectOption,
    SubmitButton,
    TimeFieldFormik,
    Title,
    yupMaxLengthField,
    yupMinValueField,
    yupRequiredField,
} from "ui/componentes";
import {
    InvolvedCarsSelections,
    NotifiedBySelection,
} from "ui/views/ChampionshipForm/RoundEdit/IncidentForm";
import { addBroadcastMessageIfNotExists } from "ui/views/ChampionshipForm/RoundEdit/IncidentForm/addBroadcastMessageIfNotExists";
import { InfractionSelection } from "ui/views/ChampionshipForm/RoundEdit/IncidentForm/InfractionSelection";
import * as Yup from "yup";
import pickBy from "lodash/pickBy";
import { ChampionshipDriver } from "api/model/Championship/ChampionshipDriver";

const IncidentFormValidation = Yup.object().shape({
    lap: Yup.number().required(yupRequiredField).min(0, yupMinValueField),
    involvedCars: Yup.array().required(yupRequiredField),
    infractionId: Yup.string().when("guiltyCar", {
        is: (value) => value && value !== "-1",
        then: Yup.string().required(yupRequiredField),
    }),
});

const cancelForm = (resetForm, onCancel) => () => {
    onCancel();
    resetForm();
};

const onSubmitIncident = (
    firestore: ExtendedFirestoreInstance,
    enqueueSnackbar,
    onCancel,
    initialValues: Incident,
    roundId: string,
    driversByNumber
) => (
    incident: Incident,
    { setSubmitting, resetForm }: FormikHelpers<Incident>
) => {
    const incidentCollection = firestore
        .collection(Collections.RACES_CONTROL)
        .doc(incident.raceControlId)
        .collection(Collections.INCIDENTS);
    let insertOrUpdate: Promise<any>;

    if (incident.guiltyCar === "-1") {
        incident.guiltyCar = "";
    }

    if (incident.id) {
        insertOrUpdate = incidentCollection
            .doc(incident.id)
            .update(pickBy(incident, (value) => value !== undefined));
    } else {
        insertOrUpdate = incidentCollection.add(
            pickBy(incident, (value) => value !== undefined)
        );
    }

    insertOrUpdate
        .then(() => {
            setSubmitting(false);
            onCancel();
            resetForm();
            enqueueSnackbar(
                `Incidente ${incident.id ? "atualizado" : "adicionado"}!`,
                {
                    variant: "success",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                    },
                }
            );
        })
        .then((doc) => {
            if (
                incident.status === StatusIncident.INVESTIGATING &&
                incident.involvedCars.length > 1
            ) {
                const incidentToCreateMessage = incident.id
                    ? incident
                    : { ...incident, id: doc["id"] };
                addBroadcastMessageIfNotExists(
                    firestore,
                    roundId,
                    incidentToCreateMessage,
                    driversByNumber
                ).catch(console.error);
            }
        })
        .catch((error) => {
            console.error(error);
            setSubmitting(false);
            enqueueSnackbar(
                `Não é possível ${
                    incident.id ? "atualizar" : "adicionar"
                } incidente. Bateria encerrada!`,
                {
                    variant: "error",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                    },
                }
            );
        });
};

interface IncidentFormProps {
    championship: Championship;
    incident: Incident;
    onCancel: () => void;
    roundId: string;
    drivers: ChampionshipDriver[];
}

const IncidentForm: React.FC<IncidentFormProps> = ({
    championship,
    incident,
    onCancel,
    roundId,
    drivers,
}) => {
    const firestore = useFirestore();
    const { enqueueSnackbar } = useSnackbar();
    const infractions = useCollection<Infraction[]>(
        {
            collection: Collections.INFRACTIONS,
            where: ["seasonId", "==", championship?.seasonId || "-1"],
            orderBy: ["key", "asc"],
            storeAs: `infractions_${championship?.seasonId || "-1"}`,
        },
        true
    );
    const driversByNumber = useMemo(
        () =>
            drivers.reduce(
                (previousValue, driver) => ({
                    ...previousValue,
                    [driver.usedNumber]: driver,
                }),
                {}
            ),
        [drivers]
    );

    return (
        <Formik
            enableReinitialize={true}
            initialValues={incident}
            onSubmit={onSubmitIncident(
                firestore,
                enqueueSnackbar,
                onCancel,
                incident,
                roundId,
                driversByNumber
            )}
            validationSchema={IncidentFormValidation}
        >
            {({ values, resetForm }) => {
                const involvedDriversOptions = drivers.filter(
                    (driver) =>
                        !values.involvedCars.includes(
                            typeof driver.usedNumber === "undefined"
                                ? "-1"
                                : driver.usedNumber + ""
                        )
                );

                const selectedInvolvedDrivers = drivers.filter((driver) =>
                    values.involvedCars.includes(
                        typeof driver.usedNumber === "undefined"
                            ? "-1"
                            : driver.usedNumber + ""
                    )
                );
                const notifiedByDriversOptions = drivers.filter(
                    (driver) =>
                        !values.notifiedBy.includes(
                            typeof driver.usedNumber === "undefined"
                                ? "-1"
                                : driver.usedNumber + ""
                        )
                );

                const selectedNotifiedByDrivers = drivers.filter((driver) =>
                    values.notifiedBy.includes(
                        typeof driver.usedNumber === "undefined"
                            ? "-1"
                            : driver.usedNumber + ""
                    )
                );

                const guiltyCarOptions = selectedInvolvedDrivers.map<SelectOption>(
                    (driver) => ({
                        label: `#${driver.usedNumber} - ${driver.displayName}`,
                        value: driver.usedNumber,
                    })
                );
                guiltyCarOptions.unshift({
                    label: "Sem culpado",
                    value: "-1",
                });

                return (
                    <Form>
                        <BoxCard>
                            <Grid container spacing={1}>
                                <Grid item xs={12}>
                                    <Title text="Adicionar incidente" />
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container spacing={1}>
                                        <Grid item xs={4} md={2}>
                                            <InputFieldFormik
                                                id="lap"
                                                name="lap"
                                                label="Volta"
                                                type="number"
                                                onFocus={(event) =>
                                                    event.currentTarget.select()
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={8} md={4}>
                                            <TimeFieldFormik
                                                id="time"
                                                name="time"
                                                label="Minuto / segundo do incidente "
                                                views={["minutes", "seconds"]}
                                                openTo="minutes"
                                                ampm={false}
                                                format="mm:ss"
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={4} md={3}>
                                    <NotifiedBySelection
                                        label="Notificado por"
                                        name="notifiedBy"
                                        options={notifiedByDriversOptions}
                                        selected={selectedNotifiedByDrivers}
                                    />
                                </Grid>
                                <Grid item xs={8} md={6}>
                                    <InvolvedCarsSelections
                                        name="involvedCars"
                                        label="Piloto(s) envolvido(s)"
                                        options={involvedDriversOptions}
                                        selected={selectedInvolvedDrivers}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <SelectFieldFormik
                                        id="guiltyCar"
                                        name="guiltyCar"
                                        label="Culpado"
                                        options={guiltyCarOptions}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <InfractionSelection
                                        options={infractions}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <InputFieldFormik
                                        id="comment"
                                        name="comment"
                                        label="Observações"
                                        helperText={`Max.: ${values.comment?.length} / 120`}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Button
                                        fullWidth
                                        onClick={cancelForm(
                                            resetForm,
                                            onCancel
                                        )}
                                    >
                                        Cancelar
                                    </Button>
                                </Grid>
                                <Grid item xs={6}>
                                    <SubmitButton fullWidth>
                                        {values.id ? "Atualizar" : "Adicionar"}
                                    </SubmitButton>
                                </Grid>
                            </Grid>
                        </BoxCard>
                    </Form>
                );
            }}
        </Formik>
    );
};

export { IncidentForm };
